import React, { useState } from "react";
import * as DropdownMenu from "@radix-ui/react-dropdown-menu";
import {
  Archive,
  ArchiveRestore,
  ChevronRight,
  MoreHorizontal,
  Loader2,
  Pencil,
  Play,
  RotateCw,
  Trash2,
} from "lucide-react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { format } from "date-fns";
import type { Recording } from "../../api/types";
import { formatDuration, parseUtcDate, recordingDurationSeconds } from "../../utils";
import { getBrowserHash } from "../../utils/urlHash";
import { buildSessionTitle } from "../analytics/sessionTitle";
import SessionApps from "./SessionApps";
import SessionStatus, { canAnalyzeSession, sessionStatus } from "./SessionStatus";
import SessionArchivedBadge from "./SessionArchivedBadge";

export type SessionAction = "archive" | "unarchive" | "delete";
export default function MySessionRow({
  recording,
  search,
  selected,
  onSelect,
  onAction,
  onRename,
  onAnalyze,
  starting,
  busy,
}: {
  recording: Recording;
  search: string;
  selected: boolean;
  onSelect: () => void;
  onAction: (action: SessionAction) => void;
  onRename: () => void;
  onAnalyze: () => Promise<void>;
  starting: boolean;
  busy: boolean;
}) {
  const { t } = useTranslation();
  const name = buildSessionTitle(undefined, recording.id, recording);
  const [analysisError, setAnalysisError] = useState(false);
  const status = sessionStatus(recording);
  const analyzing = starting || status === "analyzing";
  const analyzable = canAnalyzeSession(recording);
  const retry = recording.status.status === "failed" || recording.status.status === "cancelled";
  const itemClass =
    "flex cursor-pointer items-center gap-2 rounded-md px-3 py-2 text-sm outline-none data-[highlighted]:bg-muted-surface data-[disabled]:pointer-events-none data-[disabled]:opacity-40";
  const duration = recordingDurationSeconds(recording);
  return (
    <div
      className={`relative flex flex-wrap items-center gap-2 border-b border-outline px-3 py-4 last:border-0 hover:bg-info/5 @[640px]:flex-nowrap @[640px]:gap-3 @[640px]:px-4 ${selected ? "bg-info/5" : ""}`}
    >
      <input
        type="checkbox"
        checked={selected}
        onChange={onSelect}
        disabled={busy || starting}
        aria-label={t("activity.select_session", { name })}
        className="relative z-10 h-4 w-4 shrink-0 cursor-pointer accent-info"
      />
      <div className="min-w-0 flex-1">
        <Link
          to={`/recordings/${encodeURIComponent(recording.id)}/?${search}`}
          state={{ returnHash: getBrowserHash() }}
          className="block font-medium after:absolute after:inset-0 focus-visible:outline-info"
          aria-label={`${name}, ${format(parseUtcDate(recording.date_started), "HH:mm")} – ${format(parseUtcDate(recording.date_ended), "HH:mm")}${recording.include_in_processes ? "" : `, ${t("recording_archived")}`}`}
        >
          <span className="block truncate">
            {format(parseUtcDate(recording.date_started), "HH:mm")} –{" "}
            {format(parseUtcDate(recording.date_ended), "HH:mm")}
          </span>
          <span className="mt-1 flex items-center gap-2 text-sm font-normal text-text-dim">
            <span className="truncate" title={name}>
              {name}
            </span>
            {!recording.include_in_processes && <SessionArchivedBadge />}
          </span>
        </Link>
      </div>
      <span
        className="w-16 shrink-0 whitespace-nowrap text-left text-sm tabular-nums @[960px]:w-[6.5rem]"
        title={t("duration")}
      >
        {formatDuration(duration)}
      </span>
      <SessionApps recording={recording} showProcessCount />
      <div
        className={`relative z-10 order-1 w-32 shrink-0 basis-full @[640px]:order-none @[640px]:basis-auto ${analyzable || analyzing ? "flex flex-col items-end @[640px]:block" : "hidden @[640px]:block"}`}
      >
        {analyzable || analyzing ? (
          <button
            type="button"
            disabled={busy || analyzing}
            onClick={async () => {
              setAnalysisError(false);
              try {
                await onAnalyze();
              } catch {
                setAnalysisError(true);
              }
            }}
            className={`inline-flex h-6 items-center justify-center gap-1 rounded-full border px-2 text-xs font-medium transition focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-info disabled:cursor-default disabled:opacity-60 ${retry ? "border-warning/30 bg-warning/10 text-warning-strong hover:bg-warning/15" : "border-info/25 bg-info/10 text-info hover:bg-info/15"}`}
          >
            {analyzing ? (
              <Loader2 size={12} className="shrink-0 animate-spin" aria-hidden="true" />
            ) : retry ? (
              <RotateCw size={12} className="shrink-0" aria-hidden="true" />
            ) : (
              <Play size={12} className="shrink-0" aria-hidden="true" />
            )}
            {t(
              starting
                ? "activity.analysis_starting"
                : analyzing
                  ? "analyzing_ellipsis"
                  : retry
                    ? "retry"
                    : "analyze",
            )}
          </button>
        ) : (
          <SessionStatus recording={recording} />
        )}
        {analysisError && analyzable && !analyzing && (
          <p role="alert" className="mt-1 text-xs text-danger">
            {t("activity.analysis_start_failed")}
          </p>
        )}
      </div>
      <div className="relative z-10 shrink-0">
        <DropdownMenu.Root>
          <DropdownMenu.Trigger asChild>
            <button
              type="button"
              aria-label={t("activity.session_actions", { name })}
              disabled={busy || starting}
              className="flex h-8 w-8 items-center justify-center rounded-lg text-text-dim hover:bg-muted-surface focus-visible:outline-info"
            >
              <MoreHorizontal size={18} />
            </button>
          </DropdownMenu.Trigger>
          <DropdownMenu.Portal>
            <DropdownMenu.Content
              align="end"
              sideOffset={6}
              className="z-50 min-w-40 rounded-lg border border-outline bg-surface p-1 text-text shadow-lg"
            >
              <DropdownMenu.Item className={itemClass} onSelect={onRename}>
                <Pencil size={14} />
                {t("rename_recording")}
              </DropdownMenu.Item>
              <DropdownMenu.Item
                className={itemClass}
                onSelect={() => onAction(recording.include_in_processes ? "archive" : "unarchive")}
              >
                {recording.include_in_processes ? (
                  <Archive size={14} />
                ) : (
                  <ArchiveRestore size={14} />
                )}
                {t(recording.include_in_processes ? "archive_recording" : "activity.restore")}
              </DropdownMenu.Item>
              <DropdownMenu.Separator className="my-1 h-px bg-outline" />
              <DropdownMenu.Item
                className={`${itemClass} text-danger`}
                onSelect={() => onAction("delete")}
              >
                <Trash2 size={14} />
                {t("delete_recording")}
              </DropdownMenu.Item>
            </DropdownMenu.Content>
          </DropdownMenu.Portal>
        </DropdownMenu.Root>
      </div>
      <ChevronRight
        size={16}
        aria-hidden="true"
        className="pointer-events-none hidden shrink-0 text-text-dim @[640px]:block"
      />
    </div>
  );
}
